import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Database, Trash2 } from 'lucide-react';
import {
  getAdminSystemOverview,
  resetSalesDemoEnvironment,
  type AdminSystemOverview,
} from '../../api';
import { formatAdminPanelError } from '@/lib/adminApiError';
import { AdminConfirmSheet } from './AdminConfirmSheet';
import { AdminActionBanner } from './AdminActionBanner';

const RESET_CONFIRM = 'RESET_ONVIZ_SALES';

type Props = {
  hapticSelection: () => void;
  hapticSuccess?: () => void;
  onResetComplete?: () => void;
};

export function AdminSystemOverviewPanel({ hapticSelection, hapticSuccess, onResetComplete }: Props) {
  const [data, setData] = useState<AdminSystemOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [resetMsg, setResetMsg] = useState<{ text: string; tone: 'success' | 'error' } | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getAdminSystemOverview());
    } catch (err: unknown) {
      setError(
        formatAdminPanelError(
          err,
          'Не удалось проверить базу — см. Railway backend и миграции.',
        ),
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const runReset = async () => {
    setResetting(true);
    setResetMsg(null);
    try {
      const res = await resetSalesDemoEnvironment(RESET_CONFIRM);
      hapticSuccess?.();
      setResetMsg({
        text: `Готово: заказов ${res.orders_deleted}, лидов ${res.leads_deleted}`,
        tone: 'success',
      });
      setShowResetConfirm(false);
      await load();
      onResetComplete?.();
    } catch (err: unknown) {
      setResetMsg({
        text: formatAdminPanelError(err, 'Сброс не выполнен'),
        tone: 'error',
      });
    } finally {
      setResetting(false);
    }
  };

  if (loading && !data) {
    return (
      <p className="text-xs px-1 py-2" style={{ color: 'var(--text-3)' }}>
        Проверка базы…
      </p>
    );
  }

  if (error) {
    return (
      <div
        className="rounded-2xl p-4 text-xs"
        style={{ background: 'rgba(252,129,74,0.1)', border: '1px solid rgba(252,129,74,0.35)', color: '#FC814A' }}
      >
        <AlertTriangle size={16} className="inline mr-2" />
        {error}
      </div>
    );
  }

  if (!data) return null;

  return (
    <>
      <div
        className="rounded-2xl p-4 space-y-3"
        style={{ background: 'rgba(159,122,234,0.1)', border: '1px solid rgba(159,122,234,0.35)' }}
      >
        <div className="flex items-center gap-2">
          <Database size={18} color="#D6BCFA" />
          <p className="text-sm font-bold" style={{ color: 'var(--text-1)' }}>
            Состояние базы (superadmin)
          </p>
          <button
            type="button"
            onClick={() => {
              hapticSelection();
              void load();
            }}
            className="press ml-auto text-[10px] font-bold px-2 py-1 rounded-lg"
            style={{ color: 'var(--cyan)' }}
          >
            Обновить
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-xl py-2" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <p className="text-lg font-black" style={{ color: 'var(--cyan-bright)' }}>{data.orders_count}</p>
            <p className="text-[10px]" style={{ color: 'var(--text-3)' }}>заказов</p>
          </div>
          <div className="rounded-xl py-2" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <p className="text-lg font-black" style={{ color: '#F6AD55' }}>{data.users_count}</p>
            <p className="text-[10px]" style={{ color: 'var(--text-3)' }}>пользователей</p>
          </div>
          <div className="rounded-xl py-2" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <p className="text-lg font-black" style={{ color: '#68D391' }}>{data.leads_open_count}</p>
            <p className="text-[10px]" style={{ color: 'var(--text-3)' }}>лидов</p>
          </div>
        </div>
        {data.is_empty_database ? (
          <p className="text-xs leading-relaxed" style={{ color: 'var(--text-2)' }}>
            В подключённой БД <strong>нет заказов</strong> — можно начать чистый пилот CRM.
          </p>
        ) : (
          <p className="text-xs" style={{ color: 'var(--text-3)' }}>
            Последний заказ: {data.last_order_at ? new Date(data.last_order_at).toLocaleString('ru-RU') : '—'}
          </p>
        )}
        {!data.orders_readable && data.schema_hint && (
          <p className="text-xs flex gap-2 items-start" style={{ color: '#FC814A' }}>
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            {data.schema_hint}
          </p>
        )}

        {(data.orders_count > 0 || data.leads_open_count > 0) && (
          <button
            type="button"
            disabled={resetting}
            onClick={() => {
              hapticSelection();
              setShowResetConfirm(true);
            }}
            className="press w-full flex items-center justify-center gap-2 py-3 rounded-xl text-xs font-bold touch-manipulation min-h-[48px] disabled:opacity-50"
            style={{
              background: 'rgba(252,129,74,0.15)',
              color: '#FC814A',
              border: '1px solid rgba(252,129,74,0.45)',
            }}
          >
            <Trash2 size={14} />
            {resetting ? 'Сброс…' : 'Сбросить демо: все заказы + CRM'}
          </button>
        )}
        {resetMsg && <AdminActionBanner text={resetMsg.text} tone={resetMsg.tone} />}
        <p className="text-[10px] leading-relaxed" style={{ color: 'var(--text-3)' }}>
          Один заказ — в карточке на «Заказы». Массовый сброс — код{' '}
          <span className="font-mono" style={{ color: '#F6AD55' }}>{RESET_CONFIRM}</span>.
        </p>
      </div>

      {showResetConfirm && (
        <AdminConfirmSheet
          title="Сброс демо-данных"
          description="Удалятся все заказы, лиды, задачи, лента и inbox. Пользователи и заявки «Замеры» останутся."
          confirmLabel="Сбросить всё"
          expectedPhrase={RESET_CONFIRM}
          danger
          onCancel={() => setShowResetConfirm(false)}
          onConfirm={runReset}
        />
      )}
    </>
  );
}
